import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { AuthLayout } from "../components/AuthLayout";
import { ErrorNotice, Field, useAction } from "../components/ui";
import { api, ApiError, type InvitationPreview } from "../lib/api";
import { pendingInvite, useSession } from "../lib/session";

/**
 * /join?token=... from an invite link. Shows what the invite is for, then either sends the
 * invitee to create a login / log in (keeping the token), or lets them accept.
 */
export function Join() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { session, refresh, signOut } = useSession();
  const { pending, error, setError, run } = useAction();

  const token = params.get("token") ?? pendingInvite.get();
  const [preview, setPreview] = useState<InvitationPreview | null>(null);
  const [invalid, setInvalid] = useState(false);
  const [yourName, setYourName] = useState("");

  useEffect(() => {
    if (!token) {
      setInvalid(true);
      return;
    }
    pendingInvite.set(token);
    api
      .previewInvitation(token)
      .then(setPreview)
      .catch(() => {
        pendingInvite.clear();
        setInvalid(true);
      });
  }, [token]);

  const accept = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      try {
        await api.acceptInvitation(token!, yourName.trim());
      } catch (err) {
        if (err instanceof ApiError && err.status === 403) {
          setError(`You're logged in with a different email. Sign out, then use ${preview?.email}.`);
          return;
        }
        throw err;
      }
      pendingInvite.clear();
      await refresh();
      navigate("/today", { replace: true });
    });
  };

  const masthead = (
    <h1 className="display">
      Pull up a <em>chair</em>.
    </h1>
  );

  if (invalid) {
    return (
      <AuthLayout masthead={masthead}>
        <div className="page-head">
          <h2 className="title">
            Invite <em>unavailable</em>
          </h2>
          <p className="lede">This invite link has expired, been used, or been withdrawn. Ask whoever invited you for a new one.</p>
        </div>
        <Link className="btn secondary" to="/login">
          Go to log in
        </Link>
      </AuthLayout>
    );
  }

  if (!preview) {
    return (
      <AuthLayout masthead={masthead}>
        <p className="label" aria-live="polite">
          Checking your invitation…
        </p>
      </AuthLayout>
    );
  }

  const emailParam = `email=${encodeURIComponent(preview.email)}&next=/join`;

  return (
    <AuthLayout masthead={masthead}>
      <div className="page-head">
        <p className="label">Invitation</p>
        <h2 className="title">
          Join <em>{preview.companyName}</em>
        </h2>
        <p className="lede">
          As a{preview.role === "owner" ? "n" : ""} <strong>{preview.role}</strong>, using <strong>{preview.email}</strong>.
        </p>
      </div>

      {session.status === "signedOut" && (
        <div className="form">
          <Link className="btn block" to={`/signup?${emailParam}`}>
            Create your login
          </Link>
          <Link className="btn secondary block" to={`/login?${emailParam}`}>
            I already have a login
          </Link>
        </div>
      )}

      {session.status === "noAccount" && (
        <form className="form" onSubmit={accept}>
          <Field
            label="Your name"
            hint="What your colleagues see, instead of your email."
            autoComplete="name"
            required
            maxLength={80}
            autoFocus
            value={yourName}
            onChange={(e) => setYourName(e.target.value)}
            placeholder="e.g. Sam Smith"
          />
          <ErrorNotice error={error} />
          <button className="btn block" disabled={pending}>
            {pending ? "Joining…" : `Join ${preview.companyName}`}
          </button>
          {error && (
            <button type="button" className="btn secondary block" onClick={() => void signOut()}>
              Sign out
            </button>
          )}
        </form>
      )}

      {session.status === "member" && (
        <div className="form">
          <p className="notice">
            You're already in <strong>{session.me.tenant.name}</strong>. A login can belong to one company.
          </p>
          <Link className="btn secondary block" to="/team" onClick={() => pendingInvite.clear()}>
            Back to {session.me.tenant.name}
          </Link>
        </div>
      )}
    </AuthLayout>
  );
}
